import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '@/hooks/useAuth'
import { authService } from '@/services/auth_service'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { LoginRequest, PasswordExpiryStatus } from '@magnetar/admin-api-interfaces'
import { v4 as uuidv4 } from 'uuid'
import { AlertTriangle, ShieldAlert } from 'lucide-react'

// Helper function to get browser fingerprint
const getBrowserFingerprint = () => {
  const userAgent = navigator.userAgent
  const platform = navigator.platform
  const screenResolution = `${window.screen.width}x${window.screen.height}`
  const colorDepth = window.screen.colorDepth
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  // Generate deviceId from browser information
  const fingerprint = `${userAgent}|${platform}|${screenResolution}|${colorDepth}|${timezone}`
  const storedDeviceId = localStorage.getItem('browser_device_id')

  // Return stored ID if available, otherwise generate new one
  if (storedDeviceId) {
    return storedDeviceId
  }

  // Generate new deviceId using browser info + UUID
  const deviceId = `${fingerprint}|${uuidv4()}`
  // Store in localStorage for future use
  localStorage.setItem('browser_device_id', deviceId)
  return deviceId
}

export default function Login() {
  const navigate = useNavigate()
  const { setAuthToken } = useAuth()
  const [passwordExpiryDialog, setPasswordExpiryDialog] = useState<{
    open: boolean
    status: PasswordExpiryStatus | null
  }>({
    open: false,
    status: null,
  })

  const form = useForm<LoginRequest>({
    defaultValues: {
      account: '',
      password: '',
      deviceId: '', // Will be populated on submit
      deviceType: 'web',
    },
  })

  const handleContinue = () => {
    setPasswordExpiryDialog({ open: false, status: null })
    navigate('/')
  }

  const handleChangePassword = () => {
    setPasswordExpiryDialog({ open: false, status: null })
    navigate('/settings/password')
  }

  const onSubmit = async (data: LoginRequest) => {
    try {
      // Get browser fingerprint as deviceId
      const deviceId = getBrowserFingerprint()

      // Add deviceId to login request
      const loginData = {
        ...data,
        deviceId,
        deviceType: 'web',
      }

      const response = await authService.login(loginData)
      if (response.success && response.data) {
        setAuthToken(response.data.token)
        toast.success('登入成功')

        // 密碼過期提醒已停用（僅保留連線狀態通知）
        navigate('/')
      } else {
        toast.error(response.error?.message || '發生錯誤，請稍後再試')
      }
    } catch (error) {
      const message = axios.isAxiosError<{ error?: { message?: string } }>(error)
        ? error.response?.data?.error?.message
        : undefined
      toast.error(message || '發生錯誤，請稍後再試')
    }
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
      {/* 裝飾性背景元素 */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-blue-100/30 to-cyan-100/30 rounded-full filter blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-teal-100/30 to-blue-100/30 rounded-full filter blur-3xl transform -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="relative flex flex-col md:flex-row items-center justify-center min-h-screen p-4">
        <div className="md:flex-1 max-w-md text-center px-8 md:px-8 px-4">
          <div className="transform hover:scale-105 transition-transform duration-300 mb-2 md:mb-6">
            <img src="/images/xlogo.png" alt="logo" className="mx-auto drop-shadow-xl h-auto" />
          </div>
          {/* <h1 className="text-4xl font-bold bg-gradient-to-r from-[#1698B6] to-[#0F7A8D] bg-clip-text text-transparent mb-4 tracking-tight">
            診所公播系統
          </h1>
          <p className="text-gray-600 text-lg mb-8">專業的診所多媒體管理平台</p> */}
        </div>

        <div className="md:flex-1 max-w-md px-4 md:px-8 w-full">
          <div className="backdrop-blur-sm bg-white/80 p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/20">
            <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-[#1698B6] to-[#0F7A8D] bg-clip-text text-transparent">
              登入
            </h2>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="account"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-gray-700">帳號</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="請輸入帳號"
                          className="h-11 bg-white/50 border-gray-200 focus:border-[#1698B6] focus:ring-[#1698B6]/20 transition-all duration-300"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-gray-700">密碼</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="請輸入密碼"
                          className="h-11 bg-white/50 border-gray-200 focus:border-[#1698B6] focus:ring-[#1698B6]/20 transition-all duration-300"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full h-11 bg-gradient-to-r from-[#1698B6] to-[#0F7A8D] hover:from-[#1698B6] hover:to-[#0F7A8D] hover:opacity-90 text-white font-medium rounded-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg"
                >
                  登入
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>

      {/* 密碼過期提醒 Dialog */}
      <Dialog
        open={passwordExpiryDialog.open}
        onOpenChange={(open) => {
          if (!open && passwordExpiryDialog.status?.isExpired) {
            // 密碼已過期時不允許關閉，必須更改密碼
            return
          }
          setPasswordExpiryDialog({ open, status: passwordExpiryDialog.status })
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {passwordExpiryDialog.status?.isExpired ? (
                <>
                  <ShieldAlert className="h-5 w-5 text-red-500" />
                  <span className="text-red-500">密碼已過期</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <span className="text-amber-500">密碼即將過期</span>
                </>
              )}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {passwordExpiryDialog.status?.isExpired ? (
                <span>
                  您的密碼已經過期，為了帳號安全，請立即更改密碼才能繼續使用系統。
                </span>
              ) : (
                <span>
                  您的密碼將在{' '}
                  <strong className="text-amber-600">
                    {passwordExpiryDialog.status?.daysUntilExpiry} 天
                  </strong>
                  後過期，建議您盡快更改密碼。
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
            <p className="font-medium mb-2">新密碼需符合以下規則：</p>
            <ul className="list-disc list-inside space-y-1">
              <li>至少 6 個字元</li>
              <li>需包含大寫字母</li>
              <li>需包含小寫字母</li>
              <li>需包含數字</li>
              <li>需包含特殊符號</li>
              <li>不可與最近 5 次使用的密碼相同</li>
            </ul>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            {!passwordExpiryDialog.status?.isExpired && (
              <Button variant="outline" onClick={handleContinue}>
                稍後再說
              </Button>
            )}
            <Button
              onClick={handleChangePassword}
              className="bg-gradient-to-r from-[#1698B6] to-[#0F7A8D]"
            >
              立即更改密碼
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
